import { FC } from "react";
import {
  Box,
  FormControl,
  FormErrorMessage,
  Text,
  InputGroup,
  InputRightAddon,
  Icon,
} from "@chakra-ui/react";
import { ConfigProvider, DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CiCalendar } from "react-icons/ci";
import LabelComponent from "../../atom/Label/LabelComponent";
import { borderColor } from "../../../utils/colors";
import {
  datePickerErrorStyle,
  datePickerStyle,
} from "../../../utils/styles/DatePicker/datePickerStyle";
import { Control, useController } from "react-hook-form";
import ptPT from "antd/lib/locale/pt_PT";
import "dayjs/locale/pt";

dayjs.locale("pt");

type DateMode = "year" | "month" | "date";

interface DatePickerComponentProps {
  label: string;
  fieldName: string;
  selectedDate: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  mode: DateMode;
  errors?: any;
  isRequired?: boolean;
  isDisabled?: boolean;
  extraText?: string;
  format: string;
  control: Control<any>;
  width?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

const DatePickerComponent: FC<DatePickerComponentProps> = ({
  label,
  fieldName,
  selectedDate,
  onChange,
  mode,
  errors,
  isRequired = false,
  isDisabled = false,
  extraText,
  format,
  control,
  width,
  minDate,
  maxDate,
}) => {
  const {
    field: { onChange: onChangeHookForm, name },
  } = useController({
    name: fieldName,
    control,
    defaultValue: selectedDate,
  });
  const isError = errors && !!errors[name];

  const disabledDate = (currentDate: Dayjs) => {
    if (minDate && maxDate) {
      return (
        currentDate.isBefore(minDate, "day") ||
        currentDate.isAfter(maxDate, "day")
      );
    }
    if (minDate) {
      return currentDate.isBefore(minDate, "day");
    }
    if (maxDate) {
      return currentDate.isAfter(maxDate, "day");
    }
    return false;
  };

  const handleChange = (date: Dayjs | null) => {
    onChange(date);
    onChangeHookForm(date || null);
  };

  return (
    <ConfigProvider locale={ptPT}>
      <FormControl isInvalid={isError}>
        <LabelComponent
          title={label}
          fontWeight="500"
          fontSize="md"
          {...(isRequired && { isRequired })}
        />

        <Box mt={2}>
          <InputGroup width={width}>
            <DatePicker
              id={name}
              picker={mode}
              value={selectedDate}
              onChange={handleChange}
              format={format}
              disabled={isDisabled}
              style={{
                ...datePickerStyle,
                ...(isError && datePickerErrorStyle),
              }}
              suffixIcon={null}
              placeholder="Seleccionar"
              disabledDate={disabledDate}
            />
            <InputRightAddon p={0}>
              <Icon
                aria-label="Abrir Calendário"
                as={CiCalendar}
                borderY="1px solid"
                borderX="0"
                m="4"
                borderColor={borderColor}
                sx={{ boxSize: "6", border: "0", cursor: false }}
              />
            </InputRightAddon>
          </InputGroup>
        </Box>

        {extraText && (
          <Text mt={1} fontSize="sm" color="gray.500">
            {extraText}
          </Text>
        )}

        <FormErrorMessage>{errors && errors[name]?.message}</FormErrorMessage>
      </FormControl>
    </ConfigProvider>
  );
};

export default DatePickerComponent;
